import {
  Container,
  Autocomplete,
  TextField,
  Typography,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Alert,
  Collapse,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import CallSplitIcon from "@mui/icons-material/CallSplit";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import React, { useState, useEffect, useCallback } from "react";

import { toast } from "react-toastify";
import axios from "axios";

import useApiEp from "../../../hooks/useApiEp";
import { API_ENDPOINT } from "../../../utils/constants";

import { getErrorResponse, getHeader } from "../../../utils/util";

import AddToTargetBtn from "./AddToTargetBtn";
import AIPromptTextField from "./AIPromptTextField";
import SplitsDisplay from "./SplitsDisplay";
import DatabaseAIAssistant from "./DatabaseAIAssistant";

import { useDialog } from "../../../context/DialogContext";

export const SectionCard = ({ title, children, helpText }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      mb: 3,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
    }}
  >
    <Box display="flex" alignItems="center" mb={2}>
      <Typography variant="h6" fontWeight="600" color="primary">
        {title}
      </Typography>

      {helpText && (
        <Tooltip title={helpText} arrow>
          <IconButton size="small" sx={{ ml: 1 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>

    {children}
  </Paper>
);

const RdCampaignContactGroup = ({
  campaignPost,
  setCampaignPost,
  setActiveStep,
  contactGroup,
  groupName,
}) => {
  const { data = [], isLoading: loadingList } = useApiEp(
    "/user/get-my-assigned-dbs"
  );

  const contactList =
    groupName && groupName.trim() !== ""
      ? data.filter((item) => item.name === groupName)
      : data;

  const { showDialog } = useDialog();

  const [selectedGroup, setSelectedGroup] = useState(null);

  const [contacts, setContacts] = useState([]);

  // ------------------------------------------------------------
  // AI filtering / splitting
  // ------------------------------------------------------------

  const [aiFilterResult, setAiFilterResult] = useState();

  const [shouldSplit, setShouldSplit] = useState(false);

  const [splitMessage, setSplitMessage] = useState("");

  const [splitLoading, setSplitLoading] = useState(false);

  const [splitsData, setSplitsData] = useState([]);

  // ------------------------------------------------------------
  // Mapping / count
  // ------------------------------------------------------------

  const [mappingPositions, setMappingPositions] = useState([]);

  const [contactCount, setContactCount] = useState(null);

  const [countLoading, setCountLoading] = useState(false);

  const [countError, setCountError] = useState(null);

  // ------------------------------------------------------------
  // Load contact count when group changes
  // ------------------------------------------------------------

  useEffect(() => {
    if (selectedGroup) {
      setCountLoading(true);
      setCountError(null);

      fetch(
        `${API_ENDPOINT}/user/get-contacts-count-subcategory?categoryId=${selectedGroup._id}`,
        {
          method: "GET",
          ...getHeader(),
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to fetch contact count");
          }

          return response.json();
        })
        .then((data) => {
          setContactCount(data.count);
          setCountLoading(false);

          const columns = selectedGroup.columns || [];

          const generatedContacts = Array.from({ length: data.count }, () =>
            columns
              .filter((item) => item !== "Phone")
              .reduce(
                (acc, column) => {
                  acc[column] = "****";
                  return acc;
                },
                {
                  phoneNumber: "****",
                }
              )
          );

          setContacts(generatedContacts);
        })
        .catch((err) => {
          setCountError(err.message);
          setCountLoading(false);
        });
    }
  }, [selectedGroup]);

  // ------------------------------------------------------------
  // Restore existing campaign state
  // ------------------------------------------------------------

  useEffect(() => {
    if (campaignPost?.contactsList) {
      setContacts(campaignPost.contactsList);
    }

    if (campaignPost?.contactGroup) {
      setSelectedGroup(campaignPost.contactGroup);
    }
  }, [campaignPost?.contactsList, campaignPost?.contactGroup]);

  // ------------------------------------------------------------
  // Generate parameter mapping
  // ------------------------------------------------------------

  useEffect(() => {
    if (campaignPost.bodyParams.length > 0 && !campaignPost.isStaticParams) {
      if (
        campaignPost?.contactMapping &&
        campaignPost.contactMapping.length > 0
      ) {
        setMappingPositions(campaignPost.contactMapping);
      } else {
        setMappingPositions(
          campaignPost.bodyParams.map((item, index) => ({
            type: "dynamic",
            [`param_${index + 1}`]: "",
          }))
        );
      }
    }
  }, [
    campaignPost.bodyParams,
    campaignPost.contactMapping,
    campaignPost.isStaticParams,
  ]);

  // ------------------------------------------------------------
  // Initialize start/end index
  // ------------------------------------------------------------

  useEffect(() => {
    if (
      contacts.length > 0 &&
      (campaignPost.startIndex == null || campaignPost.endIndex == null)
    ) {
      setCampaignPost((prev) => ({
        ...prev,
        startIndex: 0,
        endIndex: contacts.length,
      }));
    }
  }, [
    campaignPost.endIndex,
    campaignPost.startIndex,
    contacts,
    setCampaignPost,
  ]);

  // ------------------------------------------------------------
  // Parameter mapping
  // ------------------------------------------------------------

  const handleMappingChange = (paramIndex, field, newValue) => {
    setMappingPositions((prev) =>
      prev.map((item, index) => {
        if (index !== paramIndex) {
          return item;
        }

        const updatedItem = {
          ...item,
        };

        updatedItem[field] = newValue;

        return updatedItem;
      })
    );
  };

  // ------------------------------------------------------------
  // Database AI Assistant success
  // ------------------------------------------------------------

  const handleAIAssistantSuccess = useCallback(
    (responseData) => {
      setAiFilterResult(responseData);

      /*
       * Audience changed.
       * Any previously generated split
       * is no longer valid.
       */
      setSplitsData([]);
      setSplitMessage("");
      setShouldSplit(false);
      setCampaignPost((prev) => {
        const campaign = {
          ...prev,
          marketingQuery: responseData.fullFilter,
        };
        delete campaign.dataSplitsList;
        return campaign;
      });

      setContactCount(responseData.count);

      const columns = selectedGroup?.columns || [];

      const generatedContacts = Array.from(
        {
          length: responseData.count,
        },
        () =>
          columns
            .filter((item) => item !== "Phone")
            .reduce(
              (acc, column) => {
                acc[column] = "****";

                return acc;
              },
              {
                phoneNumber: "****",
              }
            )
      );

      setContacts(generatedContacts);
    },
    [selectedGroup, setCampaignPost]
  );

  // ------------------------------------------------------------
  // Database AI Assistant error
  // ------------------------------------------------------------

  const handleAIAssistantError = useCallback(
    (error) => {
      showDialog(
        error?.response?.data?.title || "Error",

        `${getErrorResponse(error)}`,

        {
          type: "error",
          onConfirm: () => {},
        }
      );
    },
    [showDialog]
  );

  // ------------------------------------------------------------
  // AI campaign splitting
  // ------------------------------------------------------------

  const handleSplitSubmit = useCallback(async () => {
    if (!aiFilterResult?.count || aiFilterResult.count <= 0) {
      toast.error("Please generate your target audience first");
      return;
    }

    if (!splitMessage.trim()) {
      toast.error("Please describe how you want to split the campaign");
      return;
    }

    try {
      setSplitLoading(true);

      /*
       * Clear previous split while
       * generating new one.
       */
      setSplitsData([]);

      const payload = {
        prompt: splitMessage.trim(),

        count: aiFilterResult.count,
      };

      const response = await axios.post(
        `${API_ENDPOINT}/campaign/split-campaign`,
        payload,
        getHeader()
      );

      const splits = response?.data?.splits || [];

      if (!splits.length) {
        toast.error("No campaign splits were generated");
        return;
      }

      setSplitsData(splits);
      setCampaignPost((prev) => ({ ...prev, dataSplitsList: splits }));

      toast.success(
        `${splits.length} campaign ${
          splits.length === 1 ? "split" : "splits"
        } generated`
      );
    } catch (error) {
      showDialog(
        error?.response?.data?.title || "Unable to Generate Split",

        `${getErrorResponse(error)}`,

        {
          type: "error",
          onConfirm: () => {},
        }
      );
    } finally {
      setSplitLoading(false);
    }
  }, [aiFilterResult, splitMessage, setCampaignPost, showDialog]);

  // ------------------------------------------------------------
  // Generate target audience
  // ------------------------------------------------------------

  const generateTargetAudience = () => {
    if (!selectedGroup) {
      toast.error("Please select your marketing group");
      return;
    }

    if (shouldSplit && splitsData.length === 0) {
      toast.error(
        "Generate the split schedule, or select No to continue without splitting"
      );
      return;
    }

    const checkFields = mappingPositions.filter(
      (item, index) => item[`param_${index + 1}`] === ""
    );

    if (checkFields.length > 0) {
      toast.error("Please complete all parameter mappings");
      return;
    }

    if (campaignPost.endIndex <= campaignPost.startIndex) {
      toast.error("End position must be greater than start position");
      return;
    }

    const newContacts = [];

    const slicedContacts = contacts.slice(
      campaignPost.startIndex,
      campaignPost.endIndex
    );

    slicedContacts.forEach((citem) => {
      const contact = {
        Numbers: citem.phoneNumber,
      };

      mappingPositions.forEach((item, index) => {
        if (item.type === "dynamic") {
          const field = item[`param_${index + 1}`];

          contact[`param_${index + 1}`] = citem[field];
        } else {
          contact[`param_${index + 1}`] = item[`param_${index + 1}`];
        }
      });

      newContacts.push(contact);
    });

    setCampaignPost((prev) => ({
      ...prev,

      contacts: newContacts,

      contactsList: contacts,

      contactMapping: mappingPositions,

      contactGroup: selectedGroup,

      isMarketing: true,
    }));

    toast.success(
      `Added ${
        campaignPost.contactsCount || newContacts.length
      } contacts to campaign audience`
    );

    setActiveStep(2);
  };

  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
        Select contacts assigned to you by Smart Growth AI
      </Typography>

      {/* -------------------------------------------------------- */}
      {/* Contact Group */}
      {/* -------------------------------------------------------- */}

      <SectionCard
        title="Select Contact Group"
        helpText="Choose a contact group to use for this campaign"
      >
        <Autocomplete
          size="small"
          loading={loadingList}
          value={selectedGroup}
          onChange={(event, newValue) => {
            setSelectedGroup(newValue);

            /*
             * Reset AI state when
             * database/group changes.
             */
            setAiFilterResult(undefined);

            setSplitsData([]);
            setSplitMessage("");
            setShouldSplit(false);
            setCampaignPost((prev) => {
              const campaign = { ...prev };
              delete campaign.dataSplitsList;
              return campaign;
            });
          }}
          options={contactList || []}
          getOptionLabel={(option) => option.name || ""}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Contact Group"
              placeholder="Search for contact groups..."
              helperText="Select a group containing your target audience"
            />
          )}
          fullWidth
        />

        {countLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mt: 2,
            }}
          >
            <CircularProgress size={20} sx={{ mr: 1 }} />

            <Typography variant="body2">
              Loading contact information...
            </Typography>
          </Box>
        )}

        {countError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Error loading contacts: {countError}
          </Alert>
        )}

        {contactCount !== null && !countLoading && !countError && (
          <Alert severity="info" sx={{ mt: 2 }}>
            This group contains <strong>{contactCount} contacts</strong>
          </Alert>
        )}
      </SectionCard>

      {/* -------------------------------------------------------- */}
      {/* Mapping + AI */}
      {/* -------------------------------------------------------- */}

      {contacts.length > 0 && (
        <>
          <SectionCard
            title="Parameter Mapping"
            helpText="Map template parameters to contact fields or set static values"
          >
            <Grid container spacing={2}>
              {mappingPositions.map((param, index) => {
                const paramName =
                  campaignPost.bodyParams[index] || `Param ${index + 1}`;

                return (
                  <Grid item xs={12} key={`param-${index}`}>
                    <Box
                      sx={{
                        display: "flex",

                        alignItems: "center",

                        gap: 2,
                      }}
                    >
                      <Typography
                        fontStyle={"italic"}
                        fontWeight="500"
                        minWidth={120}
                      >
                        (<code>{paramName}</code>)
                      </Typography>

                      <FormControl
                        size="small"
                        sx={{
                          minWidth: 120,
                        }}
                      >
                        <InputLabel>Mapping Type</InputLabel>

                        <Select
                          value={param.type}
                          label="Mapping Type"
                          onChange={(e) =>
                            handleMappingChange(index, "type", e.target.value)
                          }
                        >
                          <MenuItem value="dynamic">Contact Field</MenuItem>

                          <MenuItem value="static">Static Value</MenuItem>
                        </Select>
                      </FormControl>

                      {param.type === "dynamic" && (
                        <FormControl
                          size="small"
                          sx={{
                            flexGrow: 1,
                          }}
                        >
                          <InputLabel>Select Field</InputLabel>

                          <Select
                            value={param[`param_${index + 1}`]}
                            label="Select Field"
                            onChange={(e) =>
                              handleMappingChange(
                                index,

                                `param_${index + 1}`,

                                e.target.value
                              )
                            }
                          >
                            {Object.keys(contacts[0])
                              .filter(
                                (item) =>
                                  item !== "_id" && item !== "phoneNumber"
                              )
                              .map((field) => (
                                <MenuItem key={field} value={field}>
                                  {field}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      )}

                      {param.type === "static" && (
                        <TextField
                          size="small"
                          fullWidth
                          label="Enter Value"
                          value={param[`param_${index + 1}`]}
                          onChange={(e) =>
                            handleMappingChange(
                              index,

                              `param_${index + 1}`,

                              e.target.value
                            )
                          }
                        />
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {/* -------------------------------------------------- */}
            {/* Database AI */}
            {/* -------------------------------------------------- */}

            <Box mt={3}>
              <DatabaseAIAssistant
                groupId={selectedGroup?._id}
                columns={selectedGroup?.columns}
                onSuccess={handleAIAssistantSuccess}
                onError={handleAIAssistantError}
              />

              {/* ---------------------------------------------- */}
              {/* AI Split Campaign */}
              {/* ---------------------------------------------- */}

              {aiFilterResult?.count > 1 && (
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 2.5,
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 2.5,
                    borderColor: shouldSplit ? "primary.light" : "divider",
                    bgcolor: shouldSplit
                      ? "rgba(18, 140, 126, 0.035)"
                      : "grey.50",
                    transition: "background-color 180ms ease, border-color 180ms ease",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: { xs: "flex-start", sm: "center" },
                      justifyContent: "space-between",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          borderRadius: 2,
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                        }}
                      >
                        <CallSplitIcon fontSize="small" />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Do you want to split this campaign?
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.35, maxWidth: 540, lineHeight: 1.55 }}
                        >
                          Your selected audience has{" "}
                          {aiFilterResult.count.toLocaleString()} contacts.
                          Splitting is optional; for a large audience, smaller
                          batches may help preserve your WhatsApp number quality
                          and keep delivery consistent.
                        </Typography>
                      </Box>
                    </Box>

                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={shouldSplit ? "yes" : "no"}
                      onChange={(event, value) => {
                        if (!value) return;

                        const enabled = value === "yes";
                        setShouldSplit(enabled);

                        if (!enabled) {
                          setSplitMessage("");
                          setSplitsData([]);
                          setCampaignPost((prev) => {
                            const campaign = { ...prev };
                            delete campaign.dataSplitsList;
                            return campaign;
                          });
                        }
                      }}
                      aria-label="Split campaign"
                      sx={{
                        flexShrink: 0,
                        bgcolor: "background.paper",
                        "& .MuiToggleButton-root": {
                          px: 2,
                          py: 0.75,
                          textTransform: "none",
                          fontWeight: 600,
                          borderColor: "divider",
                        },
                        "& .Mui-selected": {
                          bgcolor: "primary.main !important",
                          color: "primary.contrastText !important",
                        },
                      }}
                    >
                      <ToggleButton value="no">No</ToggleButton>
                      <ToggleButton value="yes">Yes, split</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <Collapse in={shouldSplit} unmountOnExit>
                    <Box sx={{ mt: 2.5 }}>
                      <AIPromptTextField
                        variant="outlined"
                        fullWidth
                        size="medium"
                        maxRows={4}
                        loading={splitLoading}
                        withSuggestions={false}
                        placeholder="e.g. Split by 500 contacts per campaign, start tomorrow at 10 AM and repeat every 2 hours"
                        label="How do you want to split your campaign?"
                        value={splitMessage}
                        onChange={(value) => {
                          setSplitMessage(value);

                          /*
                           * User changed the
                           * split instruction.
                           * Previous output
                           * should no longer
                           * be considered valid.
                           */
                          if (splitsData.length > 0) {
                            setSplitsData([]);
                            setCampaignPost((prev) => {
                              const campaign = { ...prev };
                              delete campaign.dataSplitsList;
                              return campaign;
                            });
                          }
                        }}
                        onSend={handleSplitSubmit}
                        helperText={`Press Enter to generate a schedule for ${aiFilterResult.count.toLocaleString()} contacts. Use Shift + Enter for a new line.`}
                      />
                    </Box>
                  </Collapse>
                </Paper>
              )}

              {/* ---------------------------------------------- */}
              {/* Generated Split Preview */}
              {/* ---------------------------------------------- */}

              {shouldSplit && splitsData.length > 0 && (
                <Box mt={3}>
                  <SplitsDisplay splitsData={splitsData} />
                </Box>
              )}
            </Box>
          </SectionCard>

          <AddToTargetBtn onClick={generateTargetAudience} />
        </>
      )}

      {/* -------------------------------------------------------- */}
      {/* Empty state */}
      {/* -------------------------------------------------------- */}

      {!selectedGroup && !countLoading && (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
          }}
        >
          <Typography variant="body1" color="textSecondary">
            {contactList?.length > 0 ? (
              "Select a contact group to configure your campaign audience"
            ) : (
              <>
                No contact lists available for selection.
                <br />
                Please{" "}
                <a href="/home/help-feedback" target="_blank" rel="noreferrer">
                  contact
                </a>{" "}
                your administrator to avail your contacts lists.
              </>
            )}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default RdCampaignContactGroup;
